import Combobox from '@/components/common/Combobox';
import {
    Form,
    FormControl,
    FormField,
    FormItem
} from '@/components/common/Form';
import IconLocationPinMap from '@/components/icon/IconLocationPinMap';
import { DEFAULT_COORDINATES } from '@/configs/constants';
import { URL_CONFIG } from '@/configs/environment';
import { useStore } from '@/hooks/useStore';
import yup from '@/services/yup';
import { LANGUAGES_SUPPORTED } from '@/types/enums';
import { yupResolver } from '@hookform/resolvers/yup';
import { AdvancedMarker, APIProvider, InfoWindow, Map, useMap } from '@vis.gl/react-google-maps';
import { CircleDot, EllipsisVertical, MapPin, MapPinIcon } from 'lucide-react';
import { observer } from 'mobx-react-lite';
import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

const DRIVE_MODE_CONST = {
    Transit: 'transit',
    Walking: 'walking'
};

const DRIVE_MODE_ARRAY = Object.values(DRIVE_MODE_CONST);

interface MapLocationProps {
    locationStore?: {
        latitude: string | number,
        longitude: string | number,
        name?: string,
        address?: string
    }
}

export default observer(function MapLocationCustom({ locationStore }: MapLocationProps) {
    const { t, i18n: { language } } = useTranslation();
    const {
        bookingStore: { deviceCurrentLocation, bikeBookingDetail, setObservable },
        locationEnduserStore: { locationDetail },
        uiStore: {
            devicesScreen: { mobile }
        }
    } = useStore();

    const validateSchema = yup.object().shape({
        mode: yup.string().required()
    });

    type FormData = yup.InferType<typeof validateSchema>;

    const form = useForm<FormData>({
        resolver: yupResolver(validateSchema),
        mode: 'onChange',
        defaultValues: {
            mode: DRIVE_MODE_CONST.Transit
        }
    });

    const watchModeDrive = form.watch('mode');
    const [center, setCenter] = useState(DEFAULT_COORDINATES);

    useEffect(() => {
        form.reset({
            mode: DRIVE_MODE_CONST.Transit
        });
    }, [locationDetail?.id, bikeBookingDetail?.id]);

    useEffect(() => {
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    setObservable(
                        'deviceCurrentLocation',
                        {
                            latitude: position.coords.latitude,
                            longitude: position.coords.longitude
                        },
                        { isMergeObject: true }
                    );
                },
                (error) => {
                    console.error(error);
                }
            );
        }
    }, []);

    useEffect(() => {
        if (deviceCurrentLocation && locationStore?.latitude && locationStore?.longitude) {
            const originLat = Number(deviceCurrentLocation.latitude);
            const originLng = Number(deviceCurrentLocation.longitude);
            const destLat = Number(locationStore.latitude);
            const destLng = Number(locationStore.longitude);

            if (isNaN(originLat) || isNaN(originLng) || isNaN(destLat) || isNaN(destLng)) {
                console.error('Invalid coordinates for center:', { deviceCurrentLocation, locationStore });
                return;
            }

            setCenter({
                lat: (originLat + destLat) / 2,
                lng: (originLng + destLng) / 2
            });
        } else if (deviceCurrentLocation) {
            const lat = Number(deviceCurrentLocation.latitude);
            const lng = Number(deviceCurrentLocation.longitude);
            if (!isNaN(lat) && !isNaN(lng)) {
                setCenter({ lat, lng });
            }
        } else if (locationStore) {
            const lat = Number(locationStore.latitude);
            const lng = Number(locationStore.longitude);
            if (!isNaN(lat) && !isNaN(lng)) {
                setCenter({ lat, lng });
            }
        }
    }, [deviceCurrentLocation, locationStore?.latitude, locationStore?.longitude]);

    return (
        <div className='h-full w-full relative'>
            <APIProvider
                language={language === LANGUAGES_SUPPORTED.Japanese ? 'ja' : 'en'}
                region={language === LANGUAGES_SUPPORTED.Japanese ? 'JP' : 'US'}
                apiKey={`${URL_CONFIG.GOOGLE_MAP.private}`}
            >
                <Map
                    id='route-location'
                    mapId='route-location'
                    style={{ height: '100%', width: '100%' }}
                    defaultCenter={center}
                    defaultZoom={12}
                    minZoom={3}
                    mapTypeControl={false}
                    streetViewControl={false}
                    fullscreenControl={false}
                    zoomControl={true}
                    gestureHandling='cooperative'
                >
                    {(deviceCurrentLocation && locationStore) && (
                        <DirectionsComponent
                            origin={deviceCurrentLocation}
                            destination={locationStore}
                            travelMode={watchModeDrive}
                        />
                    )}
                </Map>
            </APIProvider>
            <Form {...form}>
                <form className='absolute bottom-2 left-2 lg:top-2 lg:right-2 lg:bottom-auto lg:left-auto'>
                    <FormField
                        control={form.control}
                        name='mode'
                        render={({ field }) => (
                            <FormItem>
                                <FormControl>
                                    <Combobox
                                        search={false}
                                        placeholder={t('placeholder.choose')}
                                        value={field.value ?? ''}
                                        onChange={(item) => {
                                            field.onChange(item?.code ?? '');
                                        }}
                                        items={DRIVE_MODE_ARRAY.map(item => ({
                                            label: t(`drive_mode.${item}`),
                                            value: item,
                                            code: item
                                        }))}
                                        className='w-[8.25rem] h-9 text-[11px] sm:text-[13px] font-semibold shadow-[rgba(0,0,0,0.3)_0px_1px_4px_-1px]'
                                        side={mobile ? 'top' : 'bottom'}
                                        disableClearIcon
                                    />
                                </FormControl>
                            </FormItem>
                        )}
                    />
                </form>
            </Form>
        </div>
    );
});

interface DirectionsComponentProps {
    origin: {
        latitude: string | number,
        longitude: string | number
    },
    destination: {
        latitude: string | number,
        longitude: string | number,
        name?: string,
        address?: string
    },
    travelMode: string
}

const DirectionsComponent = memo(({ origin, destination, travelMode = DRIVE_MODE_CONST.Transit }: DirectionsComponentProps) => {
    const { t } = useTranslation();
    const map = useMap('route-location');
    const [originAddress, setOriginAddress] = useState<string>('');
    const [destinationAddress, setDestinationAddress] = useState<any>(null);
    const polylineRef = useRef<google.maps.Polyline | null>(null);

    const handleDirections = useCallback(() => {
        if (!map || !origin || !destination) {
            return;
        }

        const originLat = Number(origin.latitude);
        const originLng = Number(origin.longitude);
        const destLat = Number(destination.latitude);
        const destLng = Number(destination.longitude);

        if (isNaN(originLat) || isNaN(originLng) || isNaN(destLat) || isNaN(destLng)) {
            return;
        }

        const polylineOptions = travelMode === DRIVE_MODE_CONST.Walking ?
            {
                strokeColor: '#0a12d8',
                strokeOpacity: 0,
                icons: [
                    {
                        icon: {
                            path: google.maps.SymbolPath.CIRCLE,
                            scale: 4,
                            fillColor: '#0a12d8',
                            fillOpacity: 0.8,
                            strokeColor: '#0a12d8',
                            strokeOpacity: 0.8
                        },
                        offset: '0',
                        repeat: '15px'
                    }
                ]
            } :
            {
                strokeColor: '#0a12d8',
                strokeOpacity: 0.8,
                strokeWeight: 9
            };

        const directionsService = new google.maps.DirectionsService();

        // Xóa polyline cũ
        if (polylineRef.current) {
            polylineRef.current.setMap(null);
            polylineRef.current = null;
        }

        // Tạo request cho Directions API
        directionsService.route(
            {
                origin: { lat: originLat, lng: originLng },
                destination: { lat: destLat, lng: destLng },
                travelMode: google.maps.TravelMode[travelMode.toUpperCase()]
            },
            (result, status) => {
                if (status === google.maps.DirectionsStatus.OK && result) {
                    const route = result.routes[0];
                    const path = route.overview_path;

                    // Chỉ tạo polyline mới nếu không có polyline hiện tại
                    // if (!polylineRef.current) {
                    if (polylineRef.current) {
                        polylineRef.current.setMap(null);
                        polylineRef.current = null;
                    }

                    const newPolyline = new google.maps.Polyline({
                        ...polylineOptions,
                        path: path,
                        map: map
                    });
                    polylineRef.current = newPolyline;
                    // }

                    // Điều chỉnh bounds để hiển thị toàn bộ đường đi
                    const bounds = new google.maps.LatLngBounds();
                    path.forEach(point => bounds.extend(point));
                    map.fitBounds(bounds);
                } else {
                    // Nếu route thất bại, hiển thị cả hai điểm
                    const bounds = new google.maps.LatLngBounds();
                    bounds.extend(new google.maps.LatLng(originLat, originLng));
                    bounds.extend(new google.maps.LatLng(destLat, destLng));
                    map.fitBounds(bounds);
                }
            }
        );

        // Lấy địa chỉ gốc
        const geocoder = new google.maps.Geocoder();
        geocoder.geocode(
            { location: { lat: originLat, lng: originLng } },
            (results, status) => {
                if (status === google.maps.GeocoderStatus.OK && results?.[0]) {
                    setOriginAddress(results[0].formatted_address);
                } else {
                    setOriginAddress(t('words_title.no_address'));
                }
            }
        );
    }, [map, origin.latitude, origin.longitude, destination.latitude, destination.longitude, travelMode]);

    useEffect(() => {
        handleDirections();

        // Cleanup khi component unmount hoặc dependencies thay đổi
        return () => {
            if (polylineRef.current) {
                polylineRef.current.setMap(null);
                polylineRef.current = null;
            }
        };
    }, [handleDirections]);

    useEffect(() => {
        setDestinationAddress(null);
    }, [destination]);

    const handleMarkerClick = (location: any) => {
        setDestinationAddress(location);
    };

    const handleInfoWindowClose = () => {
        setDestinationAddress(null);
    };

    return (
        <>
            {origin && (
                <AdvancedMarker
                    position={{ lat: Number(origin.latitude), lng: Number(origin.longitude) }}
                />
            )}
            {destination && (
                <>
                    <AdvancedMarker
                        position={{ lat: Number(destination.latitude), lng: Number(destination.longitude) }}
                        onClick={() => handleMarkerClick(destination)}
                    >
                        <IconLocationPinMap />
                    </AdvancedMarker>
                    {destinationAddress && (
                        <InfoWindow
                            pixelOffset={[0, -55]}
                            className='w-auto shadow-sm pb-3 md:pb-0'
                            position={{
                                lat: +destinationAddress.latitude,
                                lng: +destinationAddress.longitude
                            }}
                            onCloseClick={handleInfoWindowClose}
                            headerContent={
                                <p className='text-[17px] font-semibold'>{destinationAddress?.name}</p>
                            }
                            zIndex={999}
                        >
                            <div>
                                <div className='text-[13px] mb-2 flex items-center space-x-0.5'>
                                    <div className='w-4'>
                                        <MapPinIcon className='w-4 h-4' />
                                    </div>
                                    <div className='flex-1 break-all'>
                                        {destinationAddress?.address || t('words_title.no_address')}
                                    </div>
                                </div>
                                <a
                                    className='text-[#1A73E8]'
                                    href={`https://www.google.com/maps?q=${destinationAddress.latitude},${destinationAddress.longitude}`}
                                    target='_blank'
                                    rel='noopener noreferrer'
                                >
                                    {t('words_title.view_on_ggmap')}
                                </a>
                            </div>
                        </InfoWindow>
                    )}
                </>
            )}
            {origin && destination && (
                <div
                    className='absolute top-2 left-2 bg-white p-1 rounded-md text-[11px] sm:text-[13px] max-w-[min(300px,70%)] shadow-[rgba(0,0,0,0.3)_0px_1px_4px_-1px]'
                >
                    <div className='flex items-center space-x-2'>
                        <CircleDot className='w-3 h-3 text-gray-500' strokeWidth={3} />
                        <p className='truncate flex-1 text-[11px] sm:text-[13px]'>
                            {originAddress || t('words_title.no_address')}
                        </p>
                    </div>
                    <div className='flex items-center space-x-2'>
                        <EllipsisVertical className='w-2 h-2 ml-0.5' />
                        <div className='w-full h-[0.8px] bg-gray-300'></div>
                    </div>
                    <div className='flex items-center space-x-2'>
                        <MapPin className='w-3 h-3 text-red-500' strokeWidth={3} />
                        <p className='truncate flex-1 text-[11px] sm:text-[13px]'>
                            {destination?.address || t('words_title.no_address')}
                        </p>
                    </div>
                    <div className='flex items-center space-x-2'>
                        <div className='w-3 h-3'></div>
                        <div className='flex-1'>
                            <a
                                className='text-[#1A73E8] text-[11px] sm:text-[13px]'
                                href={`https://www.google.com/maps/dir/?api=1&origin=${origin.latitude},${origin.longitude}&destination=${destination.latitude},${destination.longitude}&travelmode=${travelMode}`}
                                target='_blank'
                                rel='noopener noreferrer'
                            >
                                {t('words_title.more_options_ggmap')}
                            </a>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
});

DirectionsComponent.displayName = 'DirectionsComponent';
